import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GenericBoundedApiService } from '@lsq-frontend/angular-shared/api-service';
import { forLifeOf, FormType, IRadioOption, LifecycleAware } from '@lsq-frontend/common-ui';
import {
    CreateOrUpdateUserStateRequest,
    GetUserStateRequest,
    GetUserStateResponse,
    UserState,
    UserStateStatus,
    UserStateStatusDisplayEnum
} from '@lsq-frontend/remote-services/enterprise/common';
import { IterablePipe } from '@lsq-frontend/common-ui';
import { Form, LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';

@LifecycleAware()
@Component({
    selector: 'lsq-create-user-state-modal',
    template: `
        <div class="create-user-state-modal">
            <form [formGroup]="formGroup">
                <lsq-input
                    [basicLabel]="true"
                    label="State Name"
                    formControlName="userStateType"
                    [group]="formGroup"
                >
                </lsq-input>
                <label class="radio-list-label">Status</label>
                <lsq-radio-list
                    formControlName="userStateStatus"
                    name="userStateStatus"
                    label="State Status"
                    [group]="formGroup"
                    [vertical]="true"
                    [options]="userStateStatusOptions"
                >
                </lsq-radio-list>
                <lsq-textarea
                    formControlName="jsonData"
                    [basicLabel]="true"
                    label="JSON Data"
                    [group]="formGroup"
                    rows="10"
                >
                </lsq-textarea>
                <lsq-button
                    id="e2e-submit-user-state-modal-button"
                    type="submit"
                    size="md"
                    (buttonClick)="createOrUpdateUserState(userStateRequestType)"
                    [group]="formGroup"
                >
                    <ng-container *ngIf="userStateRequestType === UserStateRequestType.Create"
                        >Create State</ng-container
                    >
                    <ng-container *ngIf="userStateRequestType === UserStateRequestType.Update"
                        >Update State</ng-container
                    >
                </lsq-button>
            </form>
        </div>
    `,
    styleUrls: ['lsq-create-user-state-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqCreateOrUpdateUserStateModalComponent implements OnInit, OnDestroy {
    public UserStateRequestType = FormType;
    public userStateRequestType: FormType;
    public userState: UserState;
    public createOrUpdateUserStateService: GenericBoundedApiService;
    public getUserStateService: GenericBoundedApiService;
    public formGroup: FormGroup;
    public userStateStatusOptions: IRadioOption[];

    constructor(
        public modalService: ModalService,
        public iterablePipe: IterablePipe,
        private _formBuilder: FormBuilder
    ) {}

    public ngOnInit(): void {
        this.userStateRequestType = this.modalService.modal.data.requestType;
        this.userState = this.modalService.modal.data.userState;
        this.createOrUpdateUserStateService = this.modalService.modal.data.createOrUpdateUserStateService;
        this.getUserStateService = this.modalService.modal.data.getUserStateService;

        this.userStateStatusOptions = this.iterablePipe
            .transform(UserStateStatus)
            .map<IRadioOption>((option) => ({
                name: 'userStateStatus',
                id: 'userStateStatus' + option.value,
                label: UserStateStatusDisplayEnum[option.value],
                value: option.value
            }));

        if (this.userState) {
            this.formGroup = this._formBuilder.group({
                userStateType: [
                    this.userState.userStateType,
                    [Validators.required, LsqValidators.maxLength(255)]
                ],
                userStateStatus: [
                    this.userState.userStateStatus || UserStateStatus.NotStarted,
                    Validators.required
                ],
                jsonData: [
                    JSON.stringify(JSON.parse(this.userState.jsonData || '{}'), null, 4),
                    [LsqValidators.textArea, LsqValidators.json]
                ]
            });
        } else {
            this.formGroup = this._formBuilder.group({
                userStateType: ['', [Validators.required, LsqValidators.maxLength(255)]],
                userStateStatus: [UserStateStatus.NotStarted, Validators.required],
                jsonData: ['', [LsqValidators.textArea, LsqValidators.json]]
            });
        }

        if (this.userStateRequestType === FormType.Update) {
            this.formGroup.get('userStateType').disable();
        }
    }

    public ngOnDestroy(): void {}

    public createOrUpdateUserState(requestType: number): void {
        let currentUserStateType: string;

        if (this.userStateRequestType === FormType.Create) {
            currentUserStateType = this.formGroup.controls.userStateType.value;

            this.getUserStateService.dataStream
                .pipe(forLifeOf(this))
                .subscribe((x: GetUserStateResponse) => {
                    if (x.userState.userStateKey === Form.DefaultGuid) {
                        this.callService(currentUserStateType);
                    } else {
                        this.formGroup.get('userStateType').setErrors({
                            notUnique: { message: 'This State name already exists' }
                        });
                    }
                });
            this.getUserStateService.execute(
                new GetUserStateRequest({ userStateType: currentUserStateType })
            );
        } else {
            currentUserStateType = this.userState.userStateType;
            this.callService(currentUserStateType);
        }
    }

    public callService(currentUserStateType: string): void {
        this.createOrUpdateUserStateService.execute(
            new CreateOrUpdateUserStateRequest({
                userStateType: currentUserStateType,
                userStateStatus: this.formGroup.controls.userStateStatus.value,
                jsonData: this.formGroup.controls.jsonData.value
            })
        );
    }
}
